import type { CSSProperties, KeyboardEvent, ReactNode, RefObject } from 'react';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import * as Popover from '@radix-ui/react-popover';
import classNames from 'classnames';

import { deferEscapeKey } from '@/utils/deferEscapeKey';

import { focusPendingItem, type MenuDirection, useMenuContext } from './MenuContext';

import styles from './Menu.module.scss';

export interface MenuContentProps {
    children: ReactNode;
    /** `end` lines the popup up with the trigger's far edge. */
    align?: 'start' | 'end';
    /**
     * `sidebar` is the always-dark surface for menus opened from the sidebar; it stays dark in
     * both themes, like the sidebar itself.
     */
    appearance?: 'default' | 'sidebar';
    /** `wide` is the 220px minimum for menus whose rows carry longer labels. */
    width?: 'default' | 'wide';
    className?: string;
    /**
     * Portals the popup into the element this selector finds, e.g. `'body'`. The popup is
     * fixed-positioned, so an `overflow` on an ancestor does not clip it either way.
     */
    container?: string;
    /**
     * Takes focus whenever the popup opens, by click or key, e.g. a search field at its top.
     * Without it a keyboard open lands on the first (or last) item or control.
     */
    initialFocus?: RefObject<HTMLElement | null>;
    /** The gap between trigger and popup in px; 8 unless a compact control wants less. */
    offset?: number;
    /** The popup element; a panel holding fields may want `fieldset`. */
    tag?: 'div' | 'fieldset' | 'ul';
    /** Overrides the popup's role; a panel of fields is a `group`, not a `menu`. */
    role?: string;
    'aria-label'?: string;
    style?: CSSProperties;
    onKeyDown?: (event: KeyboardEvent<HTMLElement>) => void;
}

const SIDE: Record<MenuDirection, 'top' | 'right' | 'bottom' | 'left'> = {
    down: 'bottom',
    up: 'top',
    left: 'left',
    right: 'right',
};

/** The room kept between the popup and the window's edge. */
const COLLISION_PADDING = 5;

/**
 * The popup. In a `menu` Radix runs the keyboard: the arrows, Home and End move between
 * items, Enter and Space choose, a letter jumps to the next item starting with it, and
 * Tab closes and hands focus back to the trigger. In a panel or list the keys are the
 * browser's. Escape closes either and hands focus back, unless a field inside keeps it.
 */
export function MenuContent(props: MenuContentProps) {
    const {
        children,
        align = 'start',
        appearance = 'default',
        width = 'default',
        className,
        container,
        offset = 8,
        tag,
    } = props;
    const menu = useMenuContext('MenuContent');

    const setRef = (element: HTMLElement | null) => {
        menu.contentRef.current = element;
    };

    const shared = {
        side: SIDE[menu.direction],
        align,
        sideOffset: offset,
        collisionPadding: COLLISION_PADDING,
        // The popup's role has always been menu; a panel keeps it unless it names a better one.
        role: props.role ?? 'menu',
        id: menu.contentId,
        'aria-label': props['aria-label'],
        // A menu is named by its trigger, as Radix names it, unless it has a label of its own.
        'aria-labelledby':
            menu.kind === 'menu' && props['aria-label'] === undefined ? menu.triggerId : undefined,
        style: props.style,
        className: classNames(
            styles.menu,
            { [styles.sidebar]: appearance === 'sidebar', [styles.wide]: width === 'wide' },
            className
        ),
        onOpenAutoFocus: (event: Event) => {
            const initial = props.initialFocus?.current;
            if (initial) {
                event.preventDefault();
                menu.pendingFocus.current = null;
                initial.focus();
                return;
            }
            // A keyboard open lands on the item it asked for; a click leaves focus where
            // Radix puts it for a menu (the popup) and on the trigger for a panel.
            if (menu.pendingFocus.current !== null || menu.kind !== 'menu') {
                event.preventDefault();
                focusPendingItem(menu);
            }
        },
        onEscapeKeyDown: (event: globalThis.KeyboardEvent) =>
            deferEscapeKey(event, () => {
                menu.setOpen(false);
                menu.triggerRef.current?.focus();
            }),
    };

    const handleKeyDown = (event: KeyboardEvent<HTMLElement>) => {
        props.onKeyDown?.(event);
        if (event.isPropagationStopped() || menu.kind !== 'menu') return;
        if (event.key === 'Tab') {
            event.preventDefault();
            menu.setOpen(false);
            menu.triggerRef.current?.focus();
        }
    };

    const Tag = tag ?? 'div';
    const popup =
        menu.kind === 'menu' ? (
            <DropdownMenu.Content
                {...shared}
                ref={setRef}
                loop
                asChild={tag !== undefined}
                onKeyDown={handleKeyDown}>
                {tag === undefined ? children : <Tag>{children}</Tag>}
            </DropdownMenu.Content>
        ) : (
            <Popover.Content
                {...shared}
                ref={setRef}
                asChild={tag !== undefined}
                onKeyDown={handleKeyDown}>
                {tag === undefined ? children : <Tag>{children}</Tag>}
            </Popover.Content>
        );

    if (container === undefined) return popup;

    const target = typeof document === 'undefined' ? null : document.querySelector(container);
    const portalContainer = target instanceof HTMLElement ? target : undefined;
    return menu.kind === 'menu' ? (
        <DropdownMenu.Portal container={portalContainer}>{popup}</DropdownMenu.Portal>
    ) : (
        <Popover.Portal container={portalContainer}>{popup}</Popover.Portal>
    );
}

export default MenuContent;
